
import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import SolutionsPage from './pages/SolutionsPage';
import ProcessPage from './pages/ProcessPage';
import ContactPage from './pages/ContactPage';
import { LOGO } from './constants';

const BackgroundInfrastructure = () => {
  return (
    <div className="fixed inset-0 pointer-events-none z-[-1] overflow-hidden">
      <div className="ambient-nebula"></div>
      {/* Scattered Pulse Nodes */}
      <div className="pulse-node" style={{ top: '15%', left: '25%', animationDelay: '0s' }}></div>
      <div className="pulse-node" style={{ top: '45%', left: '85%', animationDelay: '1.2s' }}></div>
      <div className="pulse-node" style={{ top: '75%', left: '15%', animationDelay: '2.5s' }}></div>
      <div className="pulse-node" style={{ top: '25%', left: '65%', animationDelay: '3.1s' }}></div>
      <div className="pulse-node" style={{ top: '85%', left: '45%', animationDelay: '0.8s' }}></div>
      
      {/* Moving Beam */}
      <div className="absolute top-0 left-0 w-full h-full opacity-[0.02]">
        <div className="absolute w-[2px] h-[200%] bg-white top-[-50%] left-[20%] rotate-[35deg] blur-xl animate-[marquee_40s_linear_infinite]"></div>
      </div>
    </div>
  );
};

const PageMetadataManager = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0);

    // Dynamic Title Management
    const baseTitle = "FrontDesk Automations";
    const routesMap: Record<string, string> = {
      '/': "High-Fidelity AI Voice & Lead Infrastructure",
      '/solutions': "AI Solutions | Voice & SMS Automation",
      '/process': "The Blueprint | 48h Deployment Logic",
      '/contact': "Initialize Plan | Deploy Your AI Systems"
    };

    const pageTitle = routesMap[pathname] || "Enterprise AI Infrastructure";
    document.title = `${baseTitle} | ${pageTitle}`;

    // Update Meta Description dynamically if needed
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      if (pathname === '/contact') {
        metaDescription.setAttribute('content', 'Ready to scale? Initialize your AI audit with FrontDesk Automations. We identify lead leakage and deploy custom voice infrastructure.');
      } else {
        metaDescription.setAttribute('content', 'Enterprise-grade AI voice and lead automation. Deploy custom AI receptionists in under 48 hours to handle calls, book appointments, and recover leads.');
      }
    }
  }, [pathname]);

  return null;
};

const App: React.FC = () => {
  return (
    <Router>
      <PageMetadataManager />
      <div className="min-h-screen selection:bg-[#32dee6]/40 text-white flex flex-col relative">
        <BackgroundInfrastructure />
        <Navbar />
        
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/process" element={<ProcessPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </main>

        <footer className="py-24 bg-black/40 backdrop-blur-md border-t border-white/5 relative z-10">
          <div className="container mx-auto px-6">
            <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-16">
              <div className="space-y-8 text-center md:text-left">
                <div className="h-10 flex justify-center md:justify-start">
                  <LOGO className="h-full w-auto" />
                </div>
                <div className="space-y-2">
                  <p className="mono text-[10px] text-gray-400 max-w-xs leading-relaxed uppercase tracking-[0.3em] font-bold">
                    Infrastructure for the automated era.
                  </p>
                  <p className="mono text-[10px] text-gray-600 max-w-xs leading-relaxed uppercase tracking-[0.3em] font-bold">
                    Built for scale. Operated by AI.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col items-center md:items-end gap-8">
                <div className="flex items-center gap-4 group">
                  <span className="flex h-3 w-3 relative">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#32dee6] opacity-30"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-[#32dee6]"></span>
                  </span>
                  <span className="mono text-[11px] font-black text-white/50 uppercase tracking-[0.4em] group-hover:text-white transition-colors">
                    SYSTEMS_READY
                  </span>
                </div>
                <Link 
                  to="/contact" 
                  className="px-12 py-5 bg-[#1d3fc4] text-white mono text-[11px] font-black rounded-xl hover:bg-[#32dee6] hover:text-black transition-all tracking-[0.3em] uppercase shadow-2xl shadow-blue-900/40 transform hover:-translate-y-1"
                >
                  Deploy Systems
                </Link>
              </div>
            </div>
            
            <div className="mt-24 pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6 mono text-[10px] text-gray-700 tracking-[0.3em] uppercase font-bold">
              <span className="hover:text-gray-400 transition-colors">© {new Date().getFullYear()} FrontDesk Automations</span>
              <div className="flex items-center gap-4">
                <div className="flex space-x-1">
                  <div className="w-1 h-1 rounded-full bg-green-500/50"></div>
                  <div className="w-1 h-1 rounded-full bg-green-500/50"></div>
                  <div className="w-1 h-1 rounded-full bg-green-500"></div>
                </div>
                <span className="text-gray-500">All Systems Operational</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
};

export default App;